/**
 * HRSettings — Page paramètres RH configurables SaaS.
 * 3 onglets : Acomptes, Variables paie, Templates emails.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings, Mail, Wallet, ReceiptText, Save, RotateCcw, Info, Sparkles } from "lucide-react";
import { toast } from "sonner";
import SimulateTemplateDialog from "@/components/SimulateTemplateDialog";

const TOKENS_ADVANCE = ["{EMPLOYEE_NAME}", "{AMOUNT}", "{DUE_DATE}", "{MONTH}", "{YEAR}", "{VALIDATION_URL}"];
const TOKENS_PAYROLL = ["{MOIS}", "{ANNEE}", "{NOM}", "{PRENOM}", "{NOM_SALARIE}", "{MATRICULE}", "{CIVILITE}",
                          "{PRENOM_DESTINATAIRE}", "{HEURES_SUP}", "{ABSENCES}", "{CONGES_PAYES}",
                          "{ARRETS_TRAVAIL}", "{AVANCE}", "{ACOMPTE}", "{PRIME}", "{COMMENTAIRE}", "{SIGNATURE}"];

const EVENTS = [
  { key: "advance_j2", label: "Acompte — J-2 (dans 2 jours)", tokens: TOKENS_ADVANCE },
  { key: "advance_j1", label: "Acompte — J-1 (demain)", tokens: TOKENS_ADVANCE },
  { key: "advance_j0", label: "Acompte — Jour J (urgent)", tokens: TOKENS_ADVANCE },
];

function AdvanceSettingsPanel({ canWrite }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cc, setCc] = useState("");
  useEffect(() => { api.get("/hr/advance-settings").then(r => {
    setS(r.data.settings);
    setCc((r.data.settings.cc_emails || []).join(", "));
  }).catch(e => toast.error(apiError(e))); }, []);
  const upd = (k, v) => setS(p => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...s, cc_emails: cc.split(",").map(x => x.trim()).filter(Boolean) };
      await api.put("/hr/advance-settings", body);
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  if (!s) return <p className="text-slate-500">Chargement...</p>;
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Paramètres notifications acomptes</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Email RH principal (destinataire des rappels)</Label>
            <Input type="email" value={s.hr_notification_email || ""}
              onChange={(e) => upd("hr_notification_email", e.target.value)}
              disabled={!canWrite} data-testid="adv-hr-email" />
          </div>
          <div><Label>Nom expéditeur</Label>
            <Input value={s.sender_name || ""} onChange={(e) => upd("sender_name", e.target.value)}
              disabled={!canWrite} data-testid="adv-sender-name" />
          </div>
        </div>
        <div><Label>Emails en copie (CC, séparés par virgule)</Label>
          <Input value={cc} onChange={(e) => setCc(e.target.value)}
            placeholder="direction@..., compta@..." disabled={!canWrite}
            data-testid="adv-cc-emails" />
        </div>
        <div className="pt-2 border-t space-y-3">
          <div className="flex items-center justify-between">
            <div><Label>Activer les rappels automatiques</Label>
              <p className="text-xs text-slate-500">Cron quotidien 08h30 UTC · rappels J-2, J-1 et Jour J</p></div>
            <Switch checked={s.reminder_enabled} onCheckedChange={(v) => upd("reminder_enabled", v)}
              disabled={!canWrite} data-testid="adv-reminder-enabled" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Heure J-2</Label>
              <Input type="time" value={s.send_time_j2 || "08:30"}
                onChange={(e) => upd("send_time_j2", e.target.value)} disabled={!canWrite}
                data-testid="adv-time-j2" />
            </div>
            <div><Label className="text-xs">Heure J-1</Label>
              <Input type="time" value={s.send_time_j1 || "08:30"}
                onChange={(e) => upd("send_time_j1", e.target.value)} disabled={!canWrite} />
            </div>
            <div><Label className="text-xs">Heure Jour J</Label>
              <Input type="time" value={s.send_time_j0 || "08:30"}
                onChange={(e) => upd("send_time_j0", e.target.value)} disabled={!canWrite} />
            </div>
          </div>
        </div>
        <div className="pt-2 border-t space-y-3">
          <div className="flex items-center justify-between">
            <div><Label>Magic-link de validation par email</Label>
              <p className="text-xs text-slate-500">Bouton "Marquer payé" dans l'email → un clic valide sans se connecter (lien valable 45 jours)</p></div>
            <Switch checked={s.magic_link_enabled} onCheckedChange={(v) => upd("magic_link_enabled", v)}
              disabled={!canWrite} data-testid="adv-magic-link-enabled" />
          </div>
          <div className="flex items-center justify-between">
            <div><Label>Injection automatique en variables paie</Label>
              <p className="text-xs text-slate-500">Au marquage payé, le montant est ajouté à la colonne "Acompte" du mois</p></div>
            <Switch checked={s.auto_inject_payroll} onCheckedChange={(v) => upd("auto_inject_payroll", v)}
              disabled={!canWrite} />
          </div>
        </div>
        {canWrite && (
          <div className="flex justify-end pt-3 border-t">
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-adv-settings-btn">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "..." : "Enregistrer"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PayrollSettingsPanel({ canWrite }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bcc, setBcc] = useState("");
  useEffect(() => { api.get("/hr/payroll-settings").then(r => {
    setS(r.data.settings);
    setBcc((r.data.settings.bcc_emails || []).join(", "));
  }).catch(e => toast.error(apiError(e))); }, []);
  const upd = (k, v) => setS(p => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...s, bcc_emails: bcc.split(",").map(x => x.trim()).filter(Boolean) };
      await api.put("/hr/payroll-settings", body);
      toast.success("Paramètres paie enregistrés");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  if (!s) return <p className="text-slate-500">Chargement...</p>;
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Envoi variables paie au cabinet comptable</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Email destinataire (cabinet paie)</Label>
            <Input type="email" value={s.recipient_email || ""}
              onChange={(e) => upd("recipient_email", e.target.value)} disabled={!canWrite}
              data-testid="pay-recipient-email" />
          </div>
          <div><Label>Prénom du contact (utilisé dans template)</Label>
            <Input value={s.recipient_first_name || ""}
              onChange={(e) => upd("recipient_first_name", e.target.value)} disabled={!canWrite} />
          </div>
        </div>
        <div><Label>Copie cachée (BCC, séparés par virgule)</Label>
          <Input value={bcc} onChange={(e) => setBcc(e.target.value)}
            placeholder="direction@..." disabled={!canWrite} />
        </div>
        <div><Label>Signature</Label>
          <Textarea rows={2} value={s.signature || ""}
            onChange={(e) => upd("signature", e.target.value)} disabled={!canWrite}
            placeholder="Dov AVERDY&#10;Directeur" />
        </div>
        <div><Label>Sujet du mail</Label>
          <Input value={s.email_subject_template || ""}
            onChange={(e) => upd("email_subject_template", e.target.value)} disabled={!canWrite}
            className="font-mono text-xs" />
        </div>
        <div><Label>Corps du mail</Label>
          <Textarea rows={12} value={s.email_body_template || ""}
            onChange={(e) => upd("email_body_template", e.target.value)} disabled={!canWrite}
            className="font-mono text-xs" data-testid="pay-body-template" />
          <div className="mt-2 flex flex-wrap gap-1">
            {TOKENS_PAYROLL.map(t => (
              <Badge key={t} variant="outline" className="text-[10px] font-mono">{t}</Badge>
            ))}
          </div>
        </div>
        <div className="pt-3 border-t grid grid-cols-2 gap-3 items-center">
          <div className="flex items-center justify-between">
            <div><Label>Envoi automatique mensuel</Label>
              <p className="text-xs text-slate-500">Le cron RH enverra les variables au jour choisi</p></div>
            <Switch checked={s.auto_send_enabled}
              onCheckedChange={(v) => upd("auto_send_enabled", v)} disabled={!canWrite}
              data-testid="pay-auto-enabled" />
          </div>
          <div><Label>Jour d'envoi (1-28)</Label>
            <Input type="number" min="1" max="28" value={s.auto_send_day || 25}
              onChange={(e) => upd("auto_send_day", parseInt(e.target.value) || 25)}
              disabled={!canWrite} />
          </div>
        </div>
        {canWrite && (
          <div className="flex justify-end pt-3 border-t">
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-pay-settings-btn">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "..." : "Enregistrer"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TemplatePanel({ canWrite }) {
  const [event, setEvent] = useState("advance_j2");
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [simOpen, setSimOpen] = useState(false);
  const load = async () => {
    try { const r = await api.get(`/hr/email-templates/${event}`); setTpl(r.data.template); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [event]);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/hr/email-templates/${event}`, {
      event, subject: tpl.subject, body_html: tpl.body_html, is_active: true });
      toast.success("Template enregistré"); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Réinitialiser au template par défaut ?")) return;
    try { await api.delete(`/hr/email-templates/${event}`); toast.success("Template réinitialisé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const cur = EVENTS.find(e => e.key === event);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Templates emails paramétrables</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Select value={event} onValueChange={setEvent}>
          <SelectTrigger data-testid="tpl-event-select"><SelectValue /></SelectTrigger>
          <SelectContent>{EVENTS.map(e => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}</SelectContent>
        </Select>
        {tpl && (
          <>
            <div className="flex items-center gap-2 text-xs text-slate-500 bg-blue-50 p-2 rounded">
              <Info className="w-4 h-4 text-blue-600" />
              {tpl.is_default ? "Template par défaut système — personnalisez-le pour votre entreprise" : "Template personnalisé actif"}
            </div>
            <div><Label>Sujet</Label>
              <Input value={tpl.subject || ""} onChange={(e) => setTpl(p => ({ ...p, subject: e.target.value }))}
                disabled={!canWrite} data-testid="tpl-subject" />
            </div>
            <div><Label>Corps HTML</Label>
              <Textarea rows={14} value={tpl.body_html || ""}
                onChange={(e) => setTpl(p => ({ ...p, body_html: e.target.value }))}
                disabled={!canWrite} className="font-mono text-xs" data-testid="tpl-body" />
              <div className="mt-2 flex flex-wrap gap-1">
                {(cur?.tokens || []).map(t => (
                  <Badge key={t} variant="outline" className="text-[10px] font-mono cursor-pointer"
                    onClick={() => navigator.clipboard.writeText(t)}>{t}</Badge>
                ))}
              </div>
            </div>
            {canWrite && (
              <div className="flex justify-between pt-3 border-t">
                <div className="flex gap-2">
                  <Button variant="outline" onClick={reset} data-testid="reset-tpl-btn">
                    <RotateCcw className="w-4 h-4 mr-1.5" /> Réinitialiser
                  </Button>
                  <Button variant="outline" onClick={() => setSimOpen(true)}
                    className="text-purple-600 border-purple-200 hover:bg-purple-50" data-testid="simulate-btn">
                    <Sparkles className="w-4 h-4 mr-1.5" /> Tester en simulation
                  </Button>
                </div>
                <Button onClick={save} disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-tpl-btn">
                  <Save className="w-4 h-4 mr-1.5" /> {saving ? "..." : "Enregistrer"}
                </Button>
              </div>
            )}
          </>
        )}
        <SimulateTemplateDialog event={event} open={simOpen} onOpenChange={setSimOpen} />
      </CardContent>
    </Card>
  );
}

export default function HRSettings() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("hr.write") || hasPermission("admin");
  return (
    <div className="space-y-6" data-testid="hr-settings-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center">
          <Settings className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Paramètres RH
          </h1>
          <p className="text-sm text-slate-500">Configurez notifications, templates emails et automatisations</p>
        </div>
      </div>
      <Tabs defaultValue="advance">
        <TabsList>
          <TabsTrigger value="advance" data-testid="tab-advance-settings"><Wallet className="w-4 h-4 mr-1.5" />Acomptes</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll-settings"><ReceiptText className="w-4 h-4 mr-1.5" />Variables paie</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates"><Mail className="w-4 h-4 mr-1.5" />Templates emails</TabsTrigger>
        </TabsList>
        <TabsContent value="advance" className="mt-4"><AdvanceSettingsPanel canWrite={canWrite} /></TabsContent>
        <TabsContent value="payroll" className="mt-4"><PayrollSettingsPanel canWrite={canWrite} /></TabsContent>
        <TabsContent value="templates" className="mt-4"><TemplatePanel canWrite={canWrite} /></TabsContent>
      </Tabs>
    </div>
  );
}
